import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateFormat';

interface QualifiedLead {
  id: string;
  email: string;
  created_at: string;
  source: string;
  page_url: string;
  user_agent: string;
  name: string;
  company: string;
  info: string;
}

const QualifiedLeads: React.FC = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [qualifiedLeads, setQualifiedLeads] = useState<QualifiedLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || !isAdmin) {
      navigate('/login');
      return;
    }
    
    fetchQualifiedLeads();
  }, [user, isAdmin, authLoading, navigate]);

  const fetchQualifiedLeads = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching qualified leads...');
      const { data, error } = await supabase
        .from('qualified_leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching qualified leads:', error);
        throw error;
      }

      console.log('Fetched qualified leads:', data?.length || 0);
      setQualifiedLeads(data || []);
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to fetch qualified leads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">Qualified Leads</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Total: {qualifiedLeads.length} leads
            </p>
          </div>
        </div>

        {error && (
          <div className="p-4 text-red-700 bg-red-100 border-l-4 border-red-500">
            {error}
          </div>
        )}

        <div className="border-t border-gray-200">
          {loading ? (
            <div className="text-center py-4">Loading qualified leads...</div>
          ) : qualifiedLeads.length === 0 ? (
            <div className="text-center py-4 text-gray-500">No qualified leads found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Info
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {qualifiedLeads.map((lead) => (
                    <tr 
                      key={lead.id}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {lead.company}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {lead.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {lead.email}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{lead.source}</div>
                        {lead.page_url && (
                          <div className="text-xs text-gray-500 mt-1">
                            From: {lead.page_url}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(lead.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {lead.info}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QualifiedLeads; 